import {
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import className from 'classnames';

import { targetElement } from '../../utils/event_target';
import Popover from '../popover/popover';
import {
  getPopoverPositionProps,
  type HDirection,
  type PositionToTrigger,
  type VDirection,
} from '../popover/utils';

import styles from './popover_trigger.module.css';

export type PopoverTriggerHandle = {
  open: (event: MouseEvent) => void;
  close: () => void;
};

type Props = PropsWithChildren<{
  hDirectionHint?: HDirection;
  vDirectionHint?: VDirection;
  onClose?: () => void;
  onOpen?: () => void;
  triggerElement?: ReactNode;
  triggerOn?: 'contextmenu' | 'hover' | 'click';

  // How the popover should be positioned when the trigger is specified
  // `after` (default) will place popover right after the trigger, so the trigger will be
  //   fully visible, it's the most common usage.
  // `overlap` will place popover slightly overlapped over the trigger, used for showing submenus.
  positionToTrigger?: PositionToTrigger;
  usePortal?: boolean;
  popoverClassName?: string;
}>;

const PopoverTrigger = forwardRef<PopoverTriggerHandle, Props>((props, ref) => {
  const {
    children,
    hDirectionHint,
    vDirectionHint,
    onClose,
    onOpen,
    triggerElement,
    triggerOn = 'click',
    positionToTrigger = 'after',
    usePortal,
    popoverClassName,
  } = props;

  const [openedState, setOpenedState] = useState(false);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [hDirection, setHDirection] = useState<HDirection>('left');
  const [vDirection, setVDirection] = useState<VDirection>('top');

  const popoverContentRef = useRef<HTMLDivElement>(null);
  const popoverWrapRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<Element>(undefined);
  // Captured at open() time: the anchor may be detached from the DOM before
  // the popover is measured (e.g. the click re-renders and remounts the row).
  const anchorRectRef = useRef<DOMRect>(undefined);

  const lastCursorCoordinates = useRef({ x: 0, y: 0 });
  const withAnchor = useRef(false);

  const open = useCallback(
    (event: MouseEvent) => {
      const currentTarget = targetElement(event.currentTarget);
      if (!currentTarget) {
        return;
      }
      onOpen?.();

      lastCursorCoordinates.current.x = event.clientX;
      lastCursorCoordinates.current.y = event.clientY;

      anchorRef.current = currentTarget;

      // There's only one case when menu invoked without anchor, it's when
      // it's done with right click. With triggerOn === 'contextmenu' anchor
      // still exists in case when it's invoked with
      // "three dots menu" button (this condition we check with event.buttons === 0).
      withAnchor.current = triggerOn !== 'contextmenu' || event.buttons === 0;

      // In case of "three dots menu" button the actual trigger is the event target instead.
      if (triggerOn === 'contextmenu' && event.buttons === 0) {
        anchorRef.current = targetElement(event.target) ?? anchorRef.current;
      }

      anchorRectRef.current = anchorRef.current?.getBoundingClientRect();

      setOpenedState(true);
    },
    [onOpen, triggerOn],
  );

  const close = useCallback(() => {
    setOpenedState(false);
    onClose?.();
  }, [onClose]);

  const getTriggerHtmlElement = useCallback(() => {
    const result = popoverWrapRef.current?.previousElementSibling;
    if (!(result instanceof HTMLElement)) {
      return undefined;
    }
    return result;
  }, []);

  const handleDocumentMouseDown = useCallback(
    (event: MouseEvent) => {
      const popoverContentElement = popoverContentRef.current;
      if (!popoverContentElement) {
        return;
      }
      const rootElement = popoverWrapRef.current;
      if (!rootElement) {
        return;
      }
      const { target } = event;
      if (!(target instanceof Element)) {
        return;
      }
      // This condition is used to disallow popover from closing when dropdown option is selected. This is useful for example in the time picker component.
      if (target.getAttribute('data-ui') === 'option-item') {
        return;
      }

      const triggerHtmlElement = getTriggerHtmlElement();
      if (
        triggerHtmlElement &&
        triggerHtmlElement.contains(target) &&
        withAnchor.current
      ) {
        return;
      }

      if (
        popoverContentElement.contains(target) ||
        rootElement.contains(target)
      ) {
        return;
      }

      close();
    },
    [close, getTriggerHtmlElement],
  );

  const handleDocumentKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (openedState && event.code === 'Escape') {
        close();
      }
    },
    [close, openedState],
  );

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const toggleOpened = useCallback(
    (event: MouseEvent) => {
      if (openedState) {
        close();
      } else {
        open(event);
      }
    },
    [close, open, openedState],
  );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      // In Safari ctrl+click cause to fire click and contextmenu events at the same time
      // To avoid this issue we are cancelling click handler if ctrl key is pressed
      // https://stackoverflow.com/a/27973894
      if (event.ctrlKey) {
        return;
      }
      if (triggerOn === 'contextmenu') {
        close();
      }
      if (triggerOn !== 'click') {
        return;
      }
      toggleOpened(event);
    },
    [toggleOpened, triggerOn, close],
  );

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      if (triggerOn !== 'contextmenu') {
        return;
      }
      event.preventDefault();
      toggleOpened(event);
    },
    [toggleOpened, triggerOn],
  );

  const handleMouseEnter = useCallback(
    (event: MouseEvent) => {
      if (triggerOn !== 'hover') {
        return;
      }
      open(event);
    },
    [open, triggerOn],
  );

  const handleMouseLeave = useCallback(() => {
    if (triggerOn !== 'hover') {
      return;
    }
    close();
  }, [close, triggerOn]);

  const handleMeasuredOpen = (rect: DOMRect) => {
    const anchor = anchorRef.current;
    const liveRect = anchor?.isConnected
      ? anchor.getBoundingClientRect()
      : undefined;
    const anchorRect =
      liveRect && (liveRect.width || liveRect.height)
        ? liveRect
        : anchorRectRef.current;
    if (!anchorRect) {
      return;
    }
    const positionProps = getPopoverPositionProps(
      anchorRect,
      positionToTrigger,
      rect,
      lastCursorCoordinates.current,
      withAnchor.current,
      hDirectionHint,
      vDirectionHint,
    );

    setTop(positionProps.top);
    setLeft(positionProps.left);
    setHDirection(positionProps.hDirection);
    setVDirection(positionProps.vDirection);
  };

  useEffect(() => {
    if (!openedState) {
      return undefined;
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [openedState, handleDocumentMouseDown, handleDocumentKeyDown]);

  useEffect(() => {
    const rootElement = popoverWrapRef.current;
    if (!triggerElement || !rootElement) {
      return undefined;
    }

    const element = getTriggerHtmlElement();
    if (!element) {
      return undefined;
    }

    element.addEventListener('click', handleClick);
    element.addEventListener('contextmenu', handleContextMenu);
    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    rootElement.addEventListener('mouseenter', handleMouseEnter);
    rootElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('click', handleClick);
      element.removeEventListener('contextmenu', handleContextMenu);
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      rootElement.removeEventListener('mouseenter', handleMouseEnter);
      rootElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [
    handleClick,
    handleContextMenu,
    handleMouseEnter,
    handleMouseLeave,
    triggerElement,
    getTriggerHtmlElement,
  ]);

  return (
    <>
      {triggerElement}
      <div
        ref={popoverWrapRef}
        className={className(styles.root, {
          [styles.root_hover]: triggerOn === 'hover',
        })}
      >
        <Popover
          opened={openedState}
          top={top}
          left={left}
          className={popoverClassName}
          vDirection={vDirection}
          hDirection={hDirection}
          usePortal={usePortal}
          onMeasuredOpen={handleMeasuredOpen}
        >
          <div ref={popoverContentRef}>{children}</div>
        </Popover>
      </div>
    </>
  );
});

export default PopoverTrigger;
